// M4-B §13: fiscal terms 2000-2029. GOVERNMENT_TAKE_M4 step schedules supersede the M3 table from their first date
// (m3/fiscal.termsFor → m4TermsFor, M4-B hunk); PSC/PSA terms (cost recovery cap → profit oil split → tax on the
// contractor's share → Venezuela windfall) and the Iraq technical service contract are priced by m4TakePerBbl
// (m3/fiscal.hostTaxPerBbl hunk); UK EPL and Norway 78% are step entries; Alberta oil sands royalties scale with WTI
// and step from pre- to post-payout. US: corporate 35% → 21% (2018), state severance (SEVERANCE_M4, Oklahoma
// horizontal incentive, Louisiana 24-month holiday, Pennsylvania impact fee), federal offshore royalty.
import { GOVERNMENT_TAKE_M4, pscTakePerBbl } from '../../data/m4/regions.js';
import { CORPORATE_TAX_M4, SEVERANCE_M4, US_ROYALTY, US_TAX_RULES, stepAt, lerpAt, M4_FROM } from '../../data/m4/rules.js';
import { hostTakePerBbl } from '../../data/m3/regions.js';
import { wellsOnField } from '../indexes.js';
import { eraDollars, fieldDef, yf } from '../defs.js';
import { spend } from '../util.js';
import { peek, mk, r2 } from './marketCore.js';
import { wtiToday } from './marketPrices.js';

// PSAs grandfathered against later tax codes (Kazakhstan 2009 tax code: Kashagan's 1997 NCSPSA keeps its terms).
const GRANDFATHERED = { kashagan: ['tax_code_2009 (PSAs grandfathered)'] };
const FEDERAL_OFFSHORE_FIELDS = new Set(['gulf_shelf', 'deep_gulf', 'gulf_subsalt', 'gulf_lower_tertiary']);
const DWRRA_FIELDS = new Set(['deep_gulf', 'gulf_subsalt']);
export const TSC_PARTNER_SHARE = 0.25;   // Basra Oil Company carries 25% of the contract
export const OIL_SANDS_PAYOUT_MULT = 1;  // payout when cumulative field gross × (1 − royalty) ≥ cumulative well cost

// The M4 schedule entry in force for a field (respecting fieldIds and grandfathering), or null.
export function m4ScheduleEntry(regionId, y, fieldId = null) {
  const s = GOVERNMENT_TAKE_M4[regionId];
  if (!s?.length) return null;
  let v = null;
  for (const [yy, t] of s) {
    if (y < yy) break;
    if (t.fieldIds && !t.fieldIds.includes(fieldId)) continue;
    if (GRANDFATHERED[fieldId]?.includes(t.regime)) continue;
    v = t;
  }
  return v;
}

// Payout status of an oil-sands field (Alberta generic royalty regime).
export function oilSandsPayout(state, fieldId) {
  const P = peek(state)?.payout?.[fieldId];
  let cost = 0;
  for (const w of wellsOnField(state, fieldId)) cost += w.cost ?? 0;
  const gross = P?.netRevenue ?? 0;
  return { cost: Math.round(cost), netRevenue: Math.round(gross), post: cost > 0 && gross >= cost * OIL_SANDS_PAYOUT_MULT };
}
function oilSandsRates(o, wti, post) {
  const scale = (r) => (Array.isArray(r) ? r[0] + (r[1] - r[0]) * Math.max(0, Math.min(1, (wti - (o.wtiRange?.[0] ?? 55)) / ((o.wtiRange?.[1] ?? 120) - (o.wtiRange?.[0] ?? 55)))) : r);
  return post ? { royalty: 0, net: scale(o.postPayoutNet) } : { royalty: scale(o.prePayout), net: 0 };
}

// m3/fiscal.termsFor hook: M4 terms for a field from 2000, or undefined (keep the M3 terms).
export function m4TermsFor(state, fieldId) {
  const y = yf(state);
  if (y < M4_FROM) return undefined;
  const fdef = fieldDef(fieldId);
  const rid = fdef?.regionId;
  if (!rid) return undefined;
  if (rid === 'gulf_of_mexico' && FEDERAL_OFFSHORE_FIELDS.has(fieldId)) {
    const relief = DWRRA_FIELDS.has(fieldId) && y < US_ROYALTY.deepWaterRoyaltyReliefUntil;
    return { royalty: relief ? 0 : stepAt(US_ROYALTY.federalOffshore, y), incomeTax: 0, basis: 'realized', specialTax: 0, regime: relief ? 'dwrra_relief' : 'federal_offshore', m4: true, us: true };
  }
  const t = m4ScheduleEntry(rid, y, fieldId);
  if (!t) return undefined;
  const out = { ...t, m4: true };
  if (t.regime === 'technical_service_contract' && fdef.serviceContract) { out.royalty = 0; out.tsc = fdef.serviceContract; }
  if (t.oilSands && fdef.oilSands) {
    const p = oilSandsPayout(state, fieldId);
    const r = oilSandsRates(t.oilSands, Math.max(0, wtiToday(state)), p.post);
    out.royalty = r.royalty; out.specialTax = r.net; out.payout = p.post;
  }
  return out;
}

// Iraq TSC: the contractor keeps a remuneration fee on the incremental share of barrels plus cost recovery (capped at
// 50% of revenue), net of the state partner's 25%; everything else is the state's. No price upside.
export function tscKeepPerBbl(state, sc, price, cost) {
  const fee = eraDollars(state, sc.feePerBbl, sc.baseYear ?? 2009);
  const incremental = sc.plateauBbld > 0 ? Math.max(0, 1 - sc.baselineBbld / sc.plateauBbld) : 1;
  const recovery = Math.min(Math.max(0, cost), (sc.costRecoveryShare ?? 0.5) * Math.max(0, price));
  return (incremental * fee + recovery) * (1 - TSC_PARTNER_SHARE);
}

// m3/fiscal.hostTaxPerBbl hook: total government take per bbl for M4 terms (incl. royalty), or null for M3 terms.
export function m4TakePerBbl(state, terms, price, cost) {
  if (!terms?.m4) return null;
  if (terms.tsc) return Math.max(0, price - tscKeepPerBbl(state, terms.tsc, price, cost));
  if (terms.profitOilState != null) return pscTakePerBbl(terms, price, cost);
  // M4 tax-royalty regimes stack the income tax and the special tax on the same ring-fenced margin (UK CT 30% + SC +
  // EPL = 75-78%; Norway 22% + 56% = 78%), unlike the M3 sequential formula.
  const roy = (terms.royalty ?? 0) * price;
  const base = terms.royaltyCredited ? hostTakePerBbl(terms, price, price, cost) : roy + ((terms.incomeTax ?? 0) + (terms.specialTax ?? 0)) * Math.max(0, price - roy - cost);
  const windfall = terms.windfall ? terms.windfall.rate * Math.max(0, price - terms.windfall.threshold) : 0;
  return base + windfall;
}

// Track oil-sands payout (net revenue after royalty) — called with each sale via tickEcon's daily ledger.
export function notePayout(state, fieldId, netRevenue) {
  const M = mk(state);
  const P = ((M.payout ??= {})[fieldId] ??= { netRevenue: 0 });
  P.netRevenue += netRevenue;
}

// ---- US taxes -----------------------------------------------------------------------------------------------
export const corporateTaxRateM4 = (y) => stepAt(CORPORATE_TAX_M4, y) ?? 0.35;

// m2/taxes.severanceFor hook: state severance on oil from 2000 (null = keep the M2/M3 rate).
export function m4SeveranceFor(state, fieldId) {
  const y = yf(state);
  if (y < M4_FROM) return null;
  const rid = fieldDef(fieldId)?.regionId;
  const S = SEVERANCE_M4[rid];
  if (!S) return null;
  const sched = (v) => (Array.isArray(v) ? stepAt(v.map((e) => [e[0], e[1]]), y) : v);
  if (rid === 'oklahoma') return wellWeighted(state, fieldId, (w) => {
    const age = w.prod?.age ?? 9999;
    if (y >= 2011 && y < 2017.5 && isHorizontal(w) && age < 48 * 30.4) return 0.01;
    if (y >= 2017.5 && isHorizontal(w) && age < 36 * 30.4) return y >= 2018.5 ? 0.05 : 0.04;
    return 0.07;
  });
  if (rid === 'louisiana') return wellWeighted(state, fieldId, (w) => (isHorizontal(w) && (w.prod?.age ?? 9999) < (S.horizontalHolidayMonths ?? 24) * 30.4 ? 0 : S.oil));
  if (rid === 'alaska') return y >= 2007.5 ? S.oil : null; // ACES (2007) / SB21 (2013); M3 ELF before
  if (rid === 'colorado') return (S.oil ?? 0) + (S.adValorem ?? 0);
  if (typeof S.oil === 'number') return S.oil;
  if (Array.isArray(S.oil)) return sched(S.oil);
  return null;
}
const isHorizontal = (w) => !!(w.lateral || w.horizontal);
function wellWeighted(state, fieldId, fn) {
  let a = 0, b = 0;
  for (const w of wellsOnField(state, fieldId)) { const q = w.prod?.rate ?? 0; if (!(q > 0) || w.ownerOperatorId) continue; a += q * fn(w); b += q; }
  return b > 0 ? a / b : fn({ prod: { age: 9999 } });
}

// Pennsylvania unconventional-well impact fee (per producing horizontal well per year), charged daily.
export function tickImpactFee(state) {
  const y = yf(state);
  const s = SEVERANCE_M4.pennsylvania?.impactFeePerWellYear;
  if (!s || y < s[0][0]) return;
  const fee = stepAt(s, y) / 365;
  let n = 0;
  for (const w of wellsOnField(state, 'appalachia')) if (w.status === 'PRODUCING' && !w.ownerOperatorId && isHorizontal(w)) n++;
  if (n > 0) { spend(state, n * fee, 'severance', false); mk(state).ledger.impactFee = (mk(state).ledger.impactFee ?? 0) + n * fee; }
}

// ---- view ---------------------------------------------------------------------------------------------------
export function fiscalM4ViewImpl(state, fieldId, nb = null, cost = 0) {
  const y = yf(state);
  const fdef = fieldDef(fieldId);
  if (!fdef) return null;
  const rid = fdef.regionId;
  const t = m4TermsFor(state, fieldId) ?? null;
  const price = nb?.gross ?? 0;
  const take = t ? m4TakePerBbl(state, t, price, cost) : null;
  const sched = (GOVERNMENT_TAKE_M4[rid] ?? []).map(([yy, v]) => ({ from: yy, ...v }));
  return {
    fieldId, regionId: rid, year: y, active: y >= M4_FROM, terms: t, schedule: sched, price: r2(price), costPerBbl: r2(cost), takePerBbl: take != null ? r2(take) : null,
    govShare: take != null && price - cost > 0 ? r2(take / (price - cost)) : null, keepPerBbl: take != null ? r2(price - cost - take) : null,
    serviceContract: t?.tsc ? { ...t.tsc, keepPerBbl: r2(tscKeepPerBbl(state, t.tsc, price, cost)), partnerShare: TSC_PARTNER_SHARE, reservesBooked: false } : null,
    ukEpl: rid === 'north_sea_uk' && t ? { ct: t.incomeTax, supplementary: Math.min(0.1, t.specialTax), epl: Math.max(0, r2(t.specialTax - 0.1)), headline: r2(t.incomeTax + t.specialTax) } : null,
    oilSands: fdef.oilSands && t?.oilSands ? { ...oilSandsPayout(state, fieldId), royalty: t.royalty, netRoyalty: t.specialTax } : null,
    us: y >= M4_FROM ? { corporateTax: corporateTaxRateM4(y), severance: m4SeveranceFor(state, fieldId), federalOnshoreRoyalty: stepAt(US_ROYALTY.federalOnshore, y), federalOffshoreRoyalty: stepAt(US_ROYALTY.federalOffshore, y),
      idcExpensing: US_TAX_RULES.idcExpensing, pctDepletion: US_TAX_RULES.pctDepletionIndependent, bonusDepreciation: stepAt(US_TAX_RULES.bonusDepreciation, y) ?? 0 } : null,
  };
}
export { lerpAt };
